import React, { useState, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { cloneDeep } from 'lodash'

import { useActions, useAppSelector, useActionless } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { setDeckStateAction } from 'redux/deck/actions'
import { modifyCollectionCardMapAction } from 'redux/collectionV2/actions'

import PinService from 'services/pin.service'
import ToastService from 'services/toast.service'

import { PinnedCardsRecordType } from 'services/apiTypes/pinnedCards.types'
import { defaultCard, HUMAN_SORT, SORT, SortOptionType, SaveStatus, OWNED, CardType } from 'types/deck'
import { defaultCollectionV2Card } from 'redux/collectionV2/types'

import usePinnedDisplayCards from './usePinnedDisplayCards'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import LineLoader from 'components/elements/LineLoader'
import ImagePinnedCard from './CardImage/imagePinnedCard'
import BuyBulkModal from 'components/deckPage/modals/BuyBulk'
import PhatButton from 'components/formElements/PhatButton'
import CardDetailsOverlay from 'components/elements/Overlay/CardDetailsOverlay'
import Rulings from 'components/card/cardForm/Rulings'
import CardInfo from 'components/card/CardInfo/cardInfo'
import RelatedCards from 'components/collectionV2/cardDetails/RelatedCards'
import InDecks from 'components/collectionV2/cardDetails/InDecks'
import PinnedCardForm from './PinnedCardForm'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import CardFilters from 'components/filters'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import PhatDropdown from 'components/formElements/PhatDropdown'
import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'
import CreateDeckWithCardsOverlay from 'components/misc/CreateDeckWithCardsOverlay/createDeckWithCardsOverlay'

import styles from './pinnedCardsOverlay.module.scss'

const emptyPinnedCards: Record<string, PinnedCardsRecordType> = {}

type CardDetailsTabTypes = 'options' | 'info' | 'rulings' | 'inDecks' | 'collectionRecords'

const defaultCardRecord: PinnedCardsRecordType = {
  id: 0,
  notes: '',
  modifier: 'Foil',
  quantity: 0,
  card: defaultCard,
  createdAt: '',
  updatedAt: '',
  deletedAt: '',
}

const excludedSortOptions = ['Category quantity']

const sortOptions: { text: string; key: SortOptionType; value: SortOptionType }[] = Object.keys(HUMAN_SORT)
  .filter(text => !excludedSortOptions.includes(text))
  .map(text => ({
    text,
    key: HUMAN_SORT[text],
    value: HUMAN_SORT[text],
  }))

const PinnedCardsOverlay = () => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)
  const [setDeckState] = useActions(setDeckStateAction)
  const [modifyCollectionCard] = useActions(modifyCollectionCardMapAction)
  const pinnedCardsOpen = useAppSelector(state => state.active.pinnedCardsOverlay)
  const filteredCards = useAppSelector(state => state.active.pinnedCardsFilteredCards)

  const cardMap = useAppSelector(state => state.deck.cardMap)
  const collectionCards = useAppSelector(state => state.collectionV2.collectionCards)

  const [fetchingPinnedCards, setFetchingPinnedCards] = useState(true)

  const [pinnedCards, setPinnedCards] = useState<Record<string, PinnedCardsRecordType>>(emptyPinnedCards)

  const [orderBy, setOrderBy] = useState(SORT.DATE_ADDED)
  const [sortDirectionDescending, setSortDirectionDescending] = useState(false)

  const [showBulkBuyModal, setShowBulkBuyModal] = useState<boolean>(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<boolean>(false)
  const [cardModalOpen, setCardModalOpen] = useState<boolean>(false)
  const [savingCardOverlay, setSavingCardOverlay] = useState<SaveStatus | undefined>(undefined)
  const [newDeckOverlay, setNewDeckOverlay] = useState<boolean>(false)

  const [activeCard, setActiveCard] = useState<PinnedCardsRecordType>(defaultCardRecord)
  const [view, setView] = useState<CardDetailsTabTypes>('options')
  const [isDragging, setIsDragging] = useState(false)
  const [openAddToOtherDeck, setOpenAddToOtherDeck] = useState(false)

  const displayCards = usePinnedDisplayCards(pinnedCards, orderBy, sortDirectionDescending, filteredCards)

  const getCardPinnedStatus = (card: CardType, updatedCard: CardType | CardType[]) => {
    const cardsToCheck = !Array.isArray(updatedCard) ? [updatedCard] : updatedCard
    let pinnedStatus = OWNED.NO

    for (const updatedCard of cardsToCheck) {
      if (card.cardId === updatedCard.cardId) return OWNED.YES

      if (card.oracleCardId === updatedCard.oracleCardId) pinnedStatus = OWNED.PRINTING
    }

    return pinnedStatus
  }

  const handleUpdateCards = (
    updatedCards: PinnedCardsRecordType | PinnedCardsRecordType[],
    removeIds: number | number[] | undefined,
  ) => {
    const updatedCardArray = Array.isArray(updatedCards) ? updatedCards : [updatedCards]
    const removeIdsArray = Array.isArray(removeIds) ? removeIds : removeIds === undefined ? [] : [removeIds]

    const updatedCardMap = cloneDeep(cardMap)
    const updatedCollectionCards = cloneDeep(collectionCards)
    const updatedPinRecords: Record<string, PinnedCardsRecordType> = {}

    for (const updatedCard of updatedCardArray) {
      updatedPinRecords[updatedCard.id] = updatedCard

      // Get all the other pinned cards with the same oracle id to set the right pin status in deck and collection
      // Shouldn't include any cards being removed or the card we're updating (since that record will have old information)
      const otherPinnedCardRecords = Object.values(pinnedCards)
        .filter(
          cardRecord =>
            cardRecord.card.oracleCardId === updatedCard.card.oracleCardId &&
            updatedCard.id !== cardRecord.id &&
            !removeIdsArray.includes(cardRecord.id),
        )
        .map(cardRecord => cardRecord.card)

      // If the card we're updated wasn't removed, we could've changed the edition so add it to the array
      if (!removeIdsArray.includes(updatedCard.id)) otherPinnedCardRecords.push(updatedCard.card)

      // update deck card info
      for (const deckCard of Object.values(updatedCardMap)) {
        if (deckCard.oracleCardId === updatedCard.card.oracleCardId) {
          deckCard.pinnedStatus = getCardPinnedStatus(deckCard, otherPinnedCardRecords)

          updatedCardMap[deckCard.id] = deckCard
        }
      }

      // update collection card info
      for (const collectionCard of Object.values(updatedCollectionCards)) {
        if (collectionCard.card.oracleCardId === updatedCard.card.oracleCardId) {
          collectionCard.card.pinnedStatus = getCardPinnedStatus(collectionCard.card, otherPinnedCardRecords)

          modifyCollectionCard(collectionCard, removeIds)
        }
      }
    }

    setDeckState({ cardMap: updatedCardMap })

    if (removeIds !== undefined) {
      const newPinnedCards = cloneDeep(pinnedCards)
      for (const removeId of removeIdsArray) {
        delete newPinnedCards[removeId]
      }
      setPinnedCards(newPinnedCards)
      if (cardModalOpen) setCardModalOpen(false)
      return
    }

    setPinnedCards({
      ...pinnedCards,
      ...updatedPinRecords,
    })

    // Need this here to update card image in the card form when adding to collection
    setActiveCard(Object.values(updatedPinRecords)[0])
  }

  const handleRemoveAllPinnedCards = () => {
    const pinnedCardsCopy = Object.values(pinnedCards).map(card => ({ ...card, quantity: 0 }))
    const pinnedCardsRecordIds = pinnedCardsCopy.map(card => +card.id)

    PinService.removeAll()
      .then(() => {
        handleUpdateCards(pinnedCardsCopy, pinnedCardsRecordIds)
        ToastService.create('Removed all pinned cards', 'Pinned Cards', 'success')
      })
      .catch(err => ToastService.create(err.error, 'Pinned Cards', 'error'))
      .finally(() => setShowConfirmDeleteModal(false))
  }

  const openCardModal = (cardRecord: PinnedCardsRecordType) => {
    setActiveCard(cardRecord)
    setCardModalOpen(true)
  }

  useEffect(() => {
    if (!pinnedCardsOpen) return

    PinService.list()
      .then(cards => {
        const pinnedCards = cards.reduce((acc, card) => ({ ...acc, [card.id]: card }), emptyPinnedCards)

        setPinnedCards(pinnedCards)
      })
      .catch(err => {
        ToastService.create(`Unable to fetch pinned cards. ${err.error}`, 'Pinned Cards', 'error')
      })
      .finally(() => setFetchingPinnedCards(false))
  }, [pinnedCardsOpen])

  const cardActions = {
    onCardUpdated: handleUpdateCards,
    cardClick: () => null,
    openCardModal: openCardModal,
  }

  const activeCardIndex = displayCards.findIndex(cardRecord => cardRecord.id === activeCard.id)
  const prevCard = activeCardIndex > 0 ? displayCards[activeCardIndex - 1] : displayCards[displayCards.length - 1]
  const nextCard = activeCardIndex < displayCards.length - 1 ? displayCards[activeCardIndex + 1] : displayCards[0]
  const nextPrevControls = {
    previous: {
      label: prevCard?.card.name,
      onClick: () => (prevCard ? setActiveCard(prevCard) : null),
      disabled: !prevCard,
    },
    next: {
      label: nextCard?.card.name,
      onClick: () => (nextCard ? setActiveCard(nextCard) : null),
      disabled: !nextCard,
    },
  }

  const handleClose = () => {
    setActiveState({ pinnedCardsOverlay: false })
    setTimeout(() => setActiveState({ pinnedCardsFilteredCards: [] }), 500)
  }

  return (
    <GlobalOverlayStack open={pinnedCardsOpen && !isDragging} onClose={handleClose} className={styles.container}>
      <h1 className={styles.title}>Pinned cards</h1>
      <div className={styles.controlsContainer}>
        <div className={styles.controlButtons}>
          <CardFilters
            cardsToFilter={Object.values(pinnedCards).map(record => record.card)}
            filteredCards={filteredCards}
            setFilteredCards={(cardNames: string[]) => setActiveState({ pinnedCardsFilteredCards: cardNames })}
            className={styles.cardFilters}
          />

          <div className={styles.mobileSecondRow}>
            <PhatDropdown
              header="Sort by"
              icon={sortDirectionDescending ? 'sort amount down' : 'sort amount up'}
              value={orderBy}
              triggerWrapperClassName={styles.dropdowns}
              menuClassName={styles.orderByMenu}
              className={styles.orderByDropdown}
              options={[
                {
                  label: 'Order descending',
                  onChange: () => setSortDirectionDescending(!sortDirectionDescending),
                  type: 'checkbox',
                  checked: sortDirectionDescending,
                },
                { type: 'spacer' },
                ...sortOptions.map(opt => ({
                  label: opt.text,
                  id: `${opt.value}`,
                  onClick: () => setOrderBy(opt.value),
                })),
              ]}
            />

            <ArchidektDropdown
              menuClassName={styles.extrasMenu}
              options={[
                { label: 'Buy', icon: 'shopping cart', onClick: () => setShowBulkBuyModal(true) },
                { label: 'Remove all', icon: 'trash', onClick: () => setShowConfirmDeleteModal(true) },
                { label: 'Add cards to deck', icon: 'add circle', onClick: () => setOpenAddToOtherDeck(true) },
                { label: 'Create deck from cards', icon: 'add', onClick: () => setNewDeckOverlay(true) },
              ]}>
              <PhatButton className={styles.extrasMenuTrigger} icon="ellipsis vertical">
                Extras
              </PhatButton>
            </ArchidektDropdown>
          </div>
        </div>
        <span className={styles.countDisplay}>{Object.values(pinnedCards).length} / 150</span>
      </div>

      {fetchingPinnedCards && <LineLoader />}
      <div className={styles.cardGrid}>
        {displayCards.map(record => (
          <ImagePinnedCard key={record.id} pinnedCard={record} actions={cardActions} setIsDragging={setIsDragging} />
        ))}
      </div>

      <ConfirmDeleteModal
        open={showConfirmDeleteModal}
        onConfirm={handleRemoveAllPinnedCards}
        onClose={() => setShowConfirmDeleteModal(false)}
        message="Remove all pinned cards?"
      />

      <CardDetailsOverlay
        open={activeCard !== null && cardModalOpen}
        card={activeCard?.card || defaultCard}
        onClose={() => setCardModalOpen(false)}
        activeTabId={view}
        onTabChange={setView}
        saving={savingCardOverlay}
        nextPrevControls={nextPrevControls}
        tabs={[
          {
            label: 'Card options',
            id: 'options',
            body: (
              <PinnedCardForm
                cardRecord={activeCard || defaultCardRecord}
                onCardUpdated={handleUpdateCards}
                setSaving={setSavingCardOverlay}
              />
            ),
          },
          {
            label: 'In decks',
            id: 'inDecks',
            disabled: !userId,
            body: <InDecks card={activeCard?.card || defaultCard} />,
          },
          {
            label: 'Collection records',
            id: 'collectionRecords',
            disabled: !userId,
            body: (
              <RelatedCards collectionCard={{ ...defaultCollectionV2Card, card: activeCard?.card || defaultCard }} />
            ),
          },
          {
            label: 'Card info',
            id: 'info',
            body: <CardInfo card={activeCard?.card || defaultCard} />,
          },
          {
            label: 'Rulings',
            id: 'rulings',
            body: <Rulings card={activeCard?.card || defaultCard} />,
          },
        ]}
      />

      <BuyBulkModal
        linkedFrom="bulkBuyPinnedCards"
        preSelectedCards={displayCards.map(record => record.card)}
        open={showBulkBuyModal}
        onClose={() => setShowBulkBuyModal(false)}
      />

      <AddToOtherDeck
        cards={displayCards.map(card => card.card)}
        parentControlled
        open={openAddToOtherDeck}
        onClose={() => setOpenAddToOtherDeck(false)}
      />

      <CreateDeckWithCardsOverlay
        open={newDeckOverlay}
        cards={displayCards.map(card => card.card)}
        onClose={() => setNewDeckOverlay(false)}
      />
    </GlobalOverlayStack>
  )
}

export default PinnedCardsOverlay
